import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AffiliateAgreementHistory,
  AffiliateAgreementView,
  AffiliateAgreementWriteRequest,
} from '@/api/generated';
import type { CommissionPlan } from '@/api/commissionPlans';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import type { RemoteState } from '@/components/useRemote';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { commissionPlanErrorMessage } from '@/features/commissionPlans/commissionPlanErrors';
import { planSummary } from '@/features/commissionPlans/planSummary';
import { NoncompliantChip, StatusChip } from '@/components/status/StatusChip';
import { formatCivilOrDash, minorUnitDigits, moneyInputExample, parseMoneyToMinor } from '@/i18n/format';
import { intlLocale } from '@/i18n/constants';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { useCanWrite } from '@/auth/authHooks';
import { ViewOnlyNote } from '@/components/ViewOnly';
import { planRefOf } from './affiliatePlan';
import { useActionError, type ActionError } from '@/components/useActionError';

const FIELDS = ['planId', 'minPayout'] as const;

export interface DealEditorProps {
  history: AffiliateAgreementHistory | undefined;
  currency: string;
  /** The reseller's commission plans (`commissionPlansApi.listPlans`), loaded with the affiliate. */
  plans: RemoteState<CommissionPlan[]>;
  /** "Set up deal" from the list: focus the plan select once the plans load, then clear the flag. */
  focusDeal?: boolean;
  onSave: (body: AffiliateAgreementWriteRequest) => Promise<void>;
}

/** tmm-api's answers when the chosen plan can't be given (TMMAPI-0104): the plans reload. */
const PLAN_ASSIGN_ERRORS: ReadonlySet<string> = new Set([
  'COMMISSION_PLAN_NOT_FOUND',
  'COMMISSION_PLAN_NOT_ACTIVE',
  'COMMISSION_PLAN_NONCOMPLIANT',
]);

/** A plan that can be given to an affiliate: active and within the agreement's limits. */
function isAssignable(plan: Pick<CommissionPlan, 'status' | 'noncompliant'>): boolean {
  return plan.status === 'ACTIVE' && !plan.noncompliant;
}

function minorToInput(minor: number, currency: string, locale: string): string {
  if (currency === '') return '';
  const digits = minorUnitDigits(currency, locale);
  return new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(minor / 10 ** digits);
}

function fieldsFrom(
  agreement: AffiliateAgreementView | null | undefined,
  currency: string,
  locale: string,
) {
  const plan = planRefOf(agreement);
  return {
    // Preselected only while assignable: by its own status here, and among the options below.
    planId: plan && isAssignable(plan) ? String(plan.id) : '',
    cadence: agreement?.payoutCadence ?? 'WEEKLY',
    minPayout: minorToInput(agreement?.minPayoutMinor ?? 0, currency, locale),
    method: agreement?.payoutMethod ?? 'MANUAL',
    taxDocs: agreement?.taxDocsStatus ?? 'NOT_REQUIRED',
  };
}

export function DealEditor({ history, currency, plans, focusDeal = false, onSave }: DealEditorProps) {
  const { t } = useTranslation();
  const describe = useActionError(commissionPlanErrorMessage);
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const location = useLocation();
  const inForce = history?.inForce;
  const noCurrency = currency === '';
  const [fields, setFields] = useState(() => fieldsFrom(inForce, currency, locale));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const planSelect = useRef<HTMLInputElement>(null);
  const minPayoutInput = useRef<HTMLInputElement>(null);
  const fieldErrors = useFieldErrors(
    FIELDS,
    { planId: planSelect, minPayout: minPayoutInput },
    submitting,
  );
  // What was loaded: the in-force agreement's values, or the blank deal's defaults for a new one.
  const baseline = fieldsFrom(inForce, currency, locale);
  // Edited: differs from what was loaded - for a new deal, any field filled in (FRM-008).
  const dirty = JSON.stringify(fields) !== JSON.stringify(baseline);
  const guard = useUnsavedGuard({ when: canWrite && dirty, name: t('unsaved.names.deal') });

  const inForceRef = useRef(inForce);
  inForceRef.current = inForce;
  const inForceId = inForce?.id;
  // Re-seed only when the in-force agreement changes (PERF-004): other affiliate actions refresh
  // the same agreement and must not discard edits.
  useEffect(() => {
    setFields(fieldsFrom(inForceRef.current, currency, locale));
  }, [inForceId, currency, locale]);

  // A one-time focus through history state (NAV-010): cleared with `replace`, so Back and a
  // refresh never re-trigger it.
  useEffect(() => {
    if (!focusDeal || plans.status !== 'ready') return;
    planSelect.current?.focus();
    void navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [focusDeal, plans.status, navigate, location.pathname, location.search]);

  const known = plans.data ?? [];
  const assignable = known
    .filter(isAssignable)
    .sort((a, b) => a.name.localeCompare(b.name, intlLocale(locale)));
  const selected = assignable.find((plan) => String(plan.id) === fields.planId);
  const plansLoading = plans.status === 'loading';
  const plansFailed = plans.status === 'error';
  const noPlans = plans.status === 'ready' && assignable.length === 0;
  const planName = (plan: { id: number; name: string }) =>
    plan.name || t('commissionPlans.unnamed');

  // The in-force plan carries its own status (TMMAPI-0104): the chip and the warning read it.
  const current = planRefOf(inForce);
  const currentName = current ? planName(current) : '';
  const currentNotAssignable = current !== null && !isAssignable(current);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canWrite || noCurrency || plansLoading || plansFailed) return;
    const minPayoutMinor = parseMoneyToMinor(fields.minPayout, currency, locale);
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    if (!selected) found.planId = t('validation.choosePlan');
    if (minPayoutMinor === null) {
      found.minPayout = t('validation.amountMoney', {
        example: moneyInputExample(currency, locale),
      });
    }
    if (!selected || minPayoutMinor === null) {
      setError(null);
      fieldErrors.fail(found);
      return;
    }
    setSubmitting(true);
    setError(null);
    fieldErrors.reset();
    try {
      await onSave({
        commissionPlanId: selected.id,
        payoutCadence: fields.cadence,
        minPayoutMinor,
        payoutMethod: fields.method,
        taxDocsStatus: fields.taxDocs,
      });
    } catch (err) {
      const action = describe(err);
      setError(action);
      fieldErrors.failMarked(
        mapFieldErrors(action.fieldKeys, {
          commissionPlanId: 'planId',
          minPayoutMinor: 'minPayout',
        }),
        { planId: t('errors.fieldInvalid'), minPayout: t('errors.fieldInvalid') },
      );
      if (action.code !== undefined && PLAN_ASSIGN_ERRORS.has(action.code)) plans.reload();
    } finally {
      setSubmitting(false);
    }
  }

  const prior = (history?.history ?? []).filter((row) => row.id !== inForce?.id);
  const dash = t('affiliates.unavailable');

  function planField() {
    if (plansFailed) return <ApiErrorState error={plans.error} onRetry={plans.reload} />;
    if (noPlans) {
      return (
        <Alert
          severity="info"
          action={
            <Link component={RouterLink} to="/commission-plans" color="inherit">
              {t('affiliates.deal.managePlans')}
            </Link>
          }
        >
          {t('affiliates.deal.noPlans')}
        </Alert>
      );
    }
    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            select
            required
            fullWidth
            label={t('affiliates.deal.plan')}
            value={selected ? fields.planId : ''}
            inputRef={planSelect}
            onChange={(e) => {
              setFields((prev) => ({ ...prev, planId: e.target.value }));
              fieldErrors.clear('planId');
            }}
            error={fieldErrors.errors.planId !== undefined}
            helperText={fieldErrors.errors.planId}
            disabled={submitting || !canWrite || plansLoading}
            slotProps={{
              select: {
                renderValue: (value) => {
                  const plan = assignable.find((option) => String(option.id) === value);
                  return plan ? planName(plan) : '';
                },
              },
            }}
          >
            {assignable.map((plan) => (
              <MenuItem key={plan.id} value={String(plan.id)}>
                <ListItemText primary={planName(plan)} secondary={planSummary(plan, locale, t)} />
              </MenuItem>
            ))}
          </TextField>
          {plansLoading ? <CircularProgress size={20} aria-label={t('common.loading')} /> : null}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
          {selected ? (
            <Link component={RouterLink} to={`/commission-plans/${selected.id}`} variant="body2">
              {t('affiliates.deal.viewPlan')}
            </Link>
          ) : null}
          <Link component={RouterLink} to="/commission-plans" variant="body2">
            {t('affiliates.deal.managePlans')}
          </Link>
        </Stack>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="h3" component="h3">
        {t('affiliates.deal.title')}
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        <DefinitionList>
          <DefinitionField label={t('affiliates.deal.current')}>
            {current ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Link component={RouterLink} to={`/commission-plans/${current.id}`}>
                  {currentName}
                </Link>
                {currentNotAssignable ? (
                  <>
                    <StatusChip domain="rateCard" status={current.status} />
                    {current.noncompliant ? <NoncompliantChip /> : null}
                  </>
                ) : null}
              </Stack>
            ) : (
              dash
            )}
          </DefinitionField>
        </DefinitionList>
      </Box>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)} sx={{ mt: 2 }}>
        {noCurrency ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('common.noCurrency')}
          </Alert>
        ) : null}
        <ActionErrorAlert error={error} sx={{ mb: 2 }} />
        {currentNotAssignable ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('affiliates.deal.currentNotAssignable', { name: currentName })}
          </Alert>
        ) : null}
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {planField()}
          <TextField
            select
            required
            fullWidth
            label={t('affiliates.deal.cadence')}
            value={fields.cadence}
            onChange={(e) =>
              setFields((prev) => ({
                ...prev,
                cadence: e.target.value as 'WEEKLY' | 'MONTHLY',
              }))
            }
            disabled={submitting || !canWrite}
          >
            <MenuItem value="WEEKLY">{t('affiliates.deal.cadenceWeekly')}</MenuItem>
            <MenuItem value="MONTHLY">{t('affiliates.deal.cadenceMonthly')}</MenuItem>
          </TextField>
          <TextField
            required
            fullWidth
            label={t('affiliates.deal.minPayout')}
            value={fields.minPayout}
            onChange={(e) => {
              setFields((prev) => ({ ...prev, minPayout: e.target.value }));
              fieldErrors.clear('minPayout');
            }}
            inputRef={minPayoutInput}
            error={fieldErrors.errors.minPayout !== undefined}
            helperText={fieldErrors.errors.minPayout}
            disabled={submitting || !canWrite || noCurrency}
          />
          <TextField
            select
            required
            fullWidth
            label={t('affiliates.deal.method')}
            value={fields.method}
            onChange={(e) =>
              setFields((prev) => ({
                ...prev,
                method: e.target.value as AffiliateAgreementWriteRequest['payoutMethod'],
              }))
            }
            disabled={submitting || !canWrite}
          >
            <MenuItem value="MANUAL">{t('affiliates.deal.methodManual')}</MenuItem>
            <MenuItem value="STRIPE_CONNECT_MANUAL">{t('affiliates.deal.methodStripeManual')}</MenuItem>
            <MenuItem value="STRIPE_CONNECT">{t('affiliates.deal.methodStripe')}</MenuItem>
          </TextField>
          <TextField
            select
            required
            fullWidth
            label={t('affiliates.deal.taxDocs')}
            helperText={t('affiliates.deal.taxDocsHelp')}
            value={fields.taxDocs}
            onChange={(e) =>
              setFields((prev) => ({
                ...prev,
                taxDocs: e.target.value as AffiliateAgreementWriteRequest['taxDocsStatus'],
              }))
            }
            disabled={submitting || !canWrite}
          >
            <MenuItem value="NOT_REQUIRED">{t('affiliates.deal.taxNotRequired')}</MenuItem>
            <MenuItem value="PENDING">{t('affiliates.deal.taxPending')}</MenuItem>
            <MenuItem value="COLLECTED">{t('affiliates.deal.taxCollected')}</MenuItem>
          </TextField>
        </Stack>
        <Typography variant="body2" sx={{ color: tokens.text2, mt: 2 }}>
          {t('affiliates.deal.versionNote')}
        </Typography>
        {canWrite ? (
          // Disabled only for what the page itself explains: the plans still loading, failed or
          // none (their panel says so), no currency (the warning above), or - on an existing
          // deal - nothing changed (TMM-B5). Invalid input is answered on submit, never here.
          <SubmitButton
            type="submit"
            sx={{ mt: 2 }}
            loading={submitting}
            disabled={
              plansLoading ||
              plansFailed ||
              noPlans ||
              noCurrency ||
              (inForce != null && !dirty)
            }
          >
            {t('affiliates.deal.start')}
          </SubmitButton>
        ) : (
          <Box sx={{ mt: 2 }}>
            <ViewOnlyNote />
          </Box>
        )}
      </Box>
      {prior.length > 0 ? (
        <Accordion sx={{ mt: 2, boxShadow: 'none', '&::before': { display: 'none' } }}>
          <AccordionSummary>
            <Typography>{t('affiliates.deal.history')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {prior.map((row) => {
              const plan = planRefOf(row);
              const range = {
                from: formatCivilOrDash(row.effectiveFrom, locale, dash),
                until: formatCivilOrDash(row.endedAt, locale, dash),
              };
              return (
                <Typography key={row.id ?? row.effectiveFrom} variant="body2" sx={{ mb: 0.75 }}>
                  {plan.name
                    ? t('affiliates.deal.historyRangePlan', { plan: plan.name, ...range })
                    : t('affiliates.deal.historyRange', range)}
                </Typography>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ) : null}
      {guard}
    </Paper>
  );
}
